import {
	tilerepoSalvageableFrameNames,
	type FrameName,
} from "@/game/entities/config";
import {
	CHEST_CONFIG,
	getChestConfig,
	type ChestConfig,
} from "@/game/entities/objects/chest_config";
import type {
	Chest,
	ChestReward,
	SpecialChest,
} from "@/game/entities/objects/chests";
import { ChestType } from "@/game/entities/objects/chests";
import type TileRepository from "@/game/TileRepository";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useChestStore, { type ChestStoreState } from "@/store/useChestStore";
import useMapExpansionStore from "@/store/useMapExpansionStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore.ts";
import {
	TileEntityParam,
	type TileCoordinates,
	type TileIndex,
} from "@/types/tile";
import { SkinTypes } from "@/types/ui";
import { ceilNumber, floorNumber, randomInteger } from "@/utils/number";
import { storeToRefs } from "pinia";
import { ResourceType } from "./entities/config/resources";
import { TileVisibilityType } from "./entities/config/terrain";
import { UpgradeIds } from "./entities/config/upgrades";
import { TileToastType } from "./ToastMessage";
import { getUpgradeCurrentReplaceValueById } from "./utils/upgrades";

export type ChestRepositoryState = ChestStoreState;

class ChestRepository {
	private tileRepo!: TileRepository;
	private isInitialized = false;
	private chestStore = useChestStore();

	private static instance: ChestRepository;

	private constructor() {}

	public static getInstance(): ChestRepository {
		if (!this.instance) this.instance = new this();
		return this.instance;
	}

	public initialize(tileRepo: TileRepository): void {
		this.isInitialized = true;
		this.tileRepo = tileRepo;

		// After initialization, add any existing chests from the store to the map
		this.syncExistingChestsToMap();
	}

	/**
	 * Syncs chests that were loaded from backend to the map
	 * Also checks if it's a new day and resets daily values if needed
	 */
	private syncExistingChestsToMap(): void {
		// Check if it's a new day and reset daily values if needed
		const wasReset = this.chestStore.checkAndResetIfNewDay();
		if (wasReset) {
			// eslint-disable-next-line no-console
			console.log(
				"New day detected: Reset claimedChests and exploredToday values",
			);
		}

		const existingChests = this.chestStore.activeChests;

		if (existingChests.length === 0) {
			return;
		}

		existingChests.forEach((chest: Chest): void => {
			const tileIndex = this.tileRepo.getTileIndexByTileCoordinates({
				x: chest.x,
				y: chest.y,
			});
			const visibility = this.tileRepo.getParam(
				tileIndex,
				TileEntityParam.VISIBILITY,
			);

			if (visibility === TileVisibilityType.BLACK) {
				useEmitter().emit(EmitterEvents.PhaserAddDarkChestToMap, chest);
			} else {
				useEmitter().emit(EmitterEvents.PhaserAddChestToMap, chest);
			}
		});
	}

	public get initialized(): boolean {
		return this.isInitialized;
	}

	public get hasActiveSpecialChest(): boolean {
		return this.chestStore.activeSpecialChest !== null;
	}

	public get activeSpecialChestReward(): ChestReward | null {
		return this.chestStore.activeSpecialChest?.specialReward ?? null;
	}

	private get config(): ChestConfig {
		const userStore = useUserStore();
		return getChestConfig(userStore.abTestGroup);
	}

	public getRepoState(): ChestRepositoryState {
		return {
			activeChests: this.chestStore.activeChests,
			claimedChests: this.chestStore.claimedChests,
			exploredToday: this.chestStore.exploredToday,
			specialChestCooldownUntil:
				this.chestStore.specialChestCooldownUntil,
			activeSpecialChest: this.chestStore.activeSpecialChest,
			lastSaveDate: this.chestStore.lastSaveDate,
		};
	}

	public setRepoState(state: ChestRepositoryState): void {
		this.chestStore.$setState(state);
	}

	/**
	 * Updates the lastSaveDate to current date when chest data is being saved
	 * This should be called before saving chest data to server
	 */
	public updateSaveDate(): void {
		const currentDate = new Date().toISOString().split("T")[0];
		this.chestStore.setLastSaveDate(currentDate);
	}

	public getChestByCoordinates(coordinates: TileCoordinates): Chest | null {
		return (
			this.chestStore.activeChests.find(
				(c: Chest): boolean =>
					c.x === coordinates.x && c.y === coordinates.y,
			) || null
		);
	}

	public exploreTile(coordinates: TileCoordinates): void {
		this.chestStore.incrementExploredToday(1);
		this.checkForExplorationChest(coordinates);
		this.updateSaveDate(); // Update save date when exploration happens
	}

	public canGenerateChest(type: ChestType): boolean {
		if (type === ChestType.REWARDED_AD || type === ChestType.FIXED)
			return true;
		return (
			this.chestStore.activeChests.length <
			this.config.CHEST_LIMIT +
				getUpgradeCurrentReplaceValueById(UpgradeIds.treasure_hunter)
		);
	}

	public tryGenerateChest(
		type: ChestType,
		exploredTile?: TileCoordinates,
	): Chest | null {
		if (!this.canGenerateChest(type)) return null;
		let internalTile = exploredTile;
		if (type === ChestType.REWARDED_AD || type === ChestType.FIXED) {
			internalTile = {
				x: 508,
				y: 508,
			};
		}
		const coords = this.findChestCoordinates(type, internalTile);

		if (coords === null) {
			return null;
		}

		if (!coords) return null;

		const isSpecial = this.canGenerateSpecialChest(type);
		const reward = this.generateReward(isSpecial, type);
		const specialReward = isSpecial
			? this.generateSpecialReward()
			: undefined;

		const chest: Chest = {
			id: this.generateChestId(),
			type: isSpecial ? ChestType.SPECIAL : type,
			x: coords.x,
			y: coords.y,
			reward,
			specialReward,
		};

		this.chestStore.addActiveChest(chest);
		this.updateSaveDate(); // Update save date when chest is generated

		const tileIndex = this.tileRepo.getTileIndexByTileCoordinates({
			x: chest.x,
			y: chest.y,
		});
		const visibility = this.tileRepo.getParam(
			tileIndex,
			TileEntityParam.VISIBILITY,
		);
		if (visibility === TileVisibilityType.BLACK) {
			useEmitter().emit(EmitterEvents.PhaserAddDarkChestToMap, chest);
		} else {
			useEmitter().emit(EmitterEvents.PhaserAddChestToMap, chest);
		}

		if (type === ChestType.EXPLORE && isSpecial) {
			this.chestStore.setSpecialChestCooldownUntil(
				Date.now() + this.config.SPECIAL_CHEST.COOLDOWN_MS,
			);
		}
		if (type === ChestType.EXPLORE) {
			const emitter = useEmitter();
			emitter.emit(EmitterEvents.PhaserTextMessage, {
				text: "ui.chest_map_found",
				type: TileToastType.SUCCESS,
			});
		}
		return chest;
	}

	public claimChest(chestId: string): void {
		const idx = this.chestStore.activeChests.findIndex(
			(c: Chest): boolean => c.id === chestId,
		);
		if (idx === -1) return;
		this.claimProcess(idx);
	}

	private claimProcess(idx: number): void {
		const chest = this.chestStore.activeChests[idx];
		if (chest.type === ChestType.SPECIAL) {
			usePopupStore().showPopup({
				id: PopupDictionary.SPECIAL_CHEST_REWARD,
				type: PopupType.CHEST,
				skin: SkinTypes.LIGHT,
				width: 26,
				height: useResourcesStore().adSkipper > 0 ? 35 : 28,
				body: JSON.stringify(chest.specialReward),
				isPaddingless: true,
				percentageWidth: "95%",
			});
			this.chestStore.setActiveSpecialChest(chest as SpecialChest);
		} else {
			// Remove chest from active chests using store method instead of direct manipulation
			const newActiveChests = [...this.chestStore.activeChests];
			newActiveChests.splice(idx, 1);
			this.chestStore.setActiveChests(newActiveChests);
			this.chestStore.addClaimedChest(chest);
			this.addRewardToPlayer(chest.reward);
			useEmitter().emit(EmitterEvents.PhaserRemoveChestFromMap, chest);
		}
		this.updateSaveDate(); // Update save date when chest is claimed
	}

	public removeActiveSpecialChest(withSpecialReward: boolean): boolean {
		if (
			!this.chestStore.activeSpecialChest ||
			this.chestStore.activeSpecialChest === null
		)
			return false;
		const idx = this.chestStore.activeChests.findIndex(
			(c: Chest): boolean =>
				c.id === this.chestStore.activeSpecialChest?.id,
		);

		// Remove chest from active chests using store method instead of direct manipulation
		const newActiveChests = [...this.chestStore.activeChests];
		newActiveChests.splice(idx, 1);
		this.chestStore.setActiveChests(newActiveChests);
		this.chestStore.addClaimedChest(this.chestStore.activeSpecialChest!);

		if (withSpecialReward) {
			const specialReward =
				this.chestStore.activeSpecialChest?.specialReward;
			if (specialReward) {
				this.addRewardToPlayer(specialReward);
			}
		} else {
			const reducedReward: ChestReward = {
				coins:
					ceilNumber(
						this.chestStore.activeSpecialChest?.specialReward
							.coins / 5 || 0,
					) ?? 0,
			};
			this.addRewardToPlayer(reducedReward);
		}
		useEmitter().emit(
			EmitterEvents.PhaserRemoveChestFromMap,
			this.chestStore.activeSpecialChest!,
		);
		this.chestStore.setActiveSpecialChest(null);
		this.updateSaveDate(); // Update save date when special chest is removed
		return true;
	}

	public claimChestByCoordinates(x: number, y: number): void {
		const idx = this.chestStore.activeChests.findIndex(
			(c: Chest): boolean => c.x === x && c.y === y,
		);
		if (idx === -1) return;
		this.claimProcess(idx);
	}

	public resetDaily(): void {
		this.chestStore.setExploredToday(0);
		this.chestStore.setClaimedChests([]);
		this.chestStore.setLastSaveDate(new Date().toISOString().split("T")[0]);
	}

	public getNeededForNextChest(): number {
		const base = CHEST_CONFIG.EXPLORATION_CHEST_BASE;
		const increment = CHEST_CONFIG.EXPLORATION_CHEST_INCREMENT;
		const chestsClaimed = this.chestStore.claimedChests.length;
		return base + floorNumber(chestsClaimed / 3) * increment;
	}

	public getAvailableReturnChestTiles(count: number): Array<TileCoordinates> {
		const available: Array<TileCoordinates> = [];
		const checked = new Set<string>();
		const directions = this.config.RETURN.NEIGHBOR_DIRECTIONS;
		const { MAP_SIZE } = this.tileRepo;

		const exploredTiles: Array<TileCoordinates> = [];
		for (let x = 0; x < MAP_SIZE; x += this.config.RETURN.SCAN_STEP) {
			for (let y = 0; y < MAP_SIZE; y += this.config.RETURN.SCAN_STEP) {
				const tileIndex = this.tileRepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				const visibility = this.tileRepo.getParam(
					tileIndex,
					TileEntityParam.VISIBILITY,
				);
				if (visibility === TileVisibilityType.EXPLORED) {
					exploredTiles.push({ x, y });
				} else {
					for (const [dx, dy] of directions) {
						const nx = x + dx,
							ny = y + dy;
						if (
							nx >= 0 &&
							ny >= 0 &&
							nx < MAP_SIZE &&
							ny < MAP_SIZE
						) {
							const neighborIndex =
								this.tileRepo.getTileIndexByTileCoordinates({
									x: nx,
									y: ny,
								});
							const neighborVisibility = this.tileRepo.getParam(
								neighborIndex,
								TileEntityParam.VISIBILITY,
							);
							if (
								neighborVisibility ===
								TileVisibilityType.EXPLORED
							) {
								exploredTiles.push({ x: nx, y: ny });
								break;
							}
						}
					}
				}
			}
		}

		for (const tile of exploredTiles) {
			for (const [dx, dy] of directions) {
				const nx = tile.x + dx,
					ny = tile.y + dy;
				if (nx >= 0 && ny >= 0 && nx < MAP_SIZE && ny < MAP_SIZE) {
					const key = this.tileRepo.getTileIndexByTileCoordinates({
						x: tile.x,
						y: tile.y,
					});
					if (
						!checked.has(`${key}`) &&
						this.isTileAvailableForReturnChest(nx, ny)
					) {
						available.push({ x: nx, y: ny });
						checked.add(`${key}`);
						if (available.length >= count) return available;
					}
				}
			}
		}

		if (available.length < count) {
			for (let x = 0; x < MAP_SIZE && available.length < count; x++) {
				for (let y = 0; y < MAP_SIZE && available.length < count; y++) {
					const key = this.tileRepo.getTileIndexByTileCoordinates({
						x,
						y,
					});
					if (
						!checked.has(`${key}`) &&
						this.isTileAvailableForReturnChest(x, y)
					) {
						available.push({ x, y });
						checked.add(`${key}`);
						if (available.length >= count) return available;
					}
				}
			}
		}

		return available;
	}

	private isTileAvailableForReturnChest(x: number, y: number): boolean {
		if (
			this.chestStore.activeChests.some(
				(c: Chest): boolean => c.x === x && c.y === y,
			)
		)
			return false;

		// Check if tile is defined (part of the map)
		if (!this.tileRepo.isTileDefined({ x, y })) return false;

		const tileIndex = this.tileRepo.getTileIndexByTileCoordinates({ x, y });
		const visibility = this.tileRepo.getParam(
			tileIndex,
			TileEntityParam.VISIBILITY,
		);
		return visibility === TileVisibilityType.EXPLORED;
	}

	public createReturnChestAt(coords: TileCoordinates): Chest | null {
		const chest: Chest = {
			id: this.generateChestId(),
			type: ChestType.RETURN,
			x: coords.x,
			y: coords.y,
			reward: this.generateReward(false, ChestType.RETURN),
		};
		this.chestStore.addActiveChest(chest);
		useEmitter().emit(EmitterEvents.PhaserAddChestToMap, chest);
		this.updateSaveDate(); // Update save date when return chest is created
		return chest;
	}

	private checkForExplorationChest(exploredTile: TileCoordinates): void {
		if (
			this.chestStore.exploredToday % this.getNeededForNextChest() ===
			0
		) {
			this.tryGenerateChest(ChestType.EXPLORE, exploredTile);
		}
	}

	private addRewardToPlayer(reward: ChestReward): void {
		const resourcesStore = useResourcesStore();
		for (const [key, value] of Object.entries(reward) as Array<
			[string, number]
		>) {
			if (key === ResourceType.COINS) {
				const upgradeBonus =
					1 +
					getUpgradeCurrentReplaceValueById(UpgradeIds.inflation) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.geyser);

				resourcesStore.increase(
					key,
					ceilNumber(value * upgradeBonus),
					"ChestReward",
				);
			} else if (
				key === ResourceType.GEMS ||
				key === ResourceType.SUPER_CLICKER
			) {
				const upgradeBonus = 1;

				resourcesStore.increase(
					key,
					ceilNumber(value * upgradeBonus),
					"ChestReward",
				);
			}
		}
	}

	private findChestCoordinates(
		type: ChestType,
		exploredTile?: { x: number; y: number },
	): TileCoordinates | null {
		const { minX, maxX, minY, maxY } =
			useMapExpansionStore().getEffectiveMapBorderCoordinates();

		if (type === ChestType.EXPLORE && exploredTile) {
			const chestSet = new Set(
				this.chestStore.activeChests.map(
					(c: Chest): TileIndex =>
						this.tileRepo.getTileIndexByTileCoordinates({
							x: c.x,
							y: c.y,
						}),
				),
			);

			const candidates: Array<TileCoordinates> = [];
			for (
				let dx = -this.config.EXPLORATION.SCAN_RADIUS;
				dx <= this.config.EXPLORATION.SCAN_RADIUS;
				dx++
			) {
				for (
					let dy = -this.config.EXPLORATION.SCAN_RADIUS;
					dy <= this.config.EXPLORATION.SCAN_RADIUS;
					dy++
				) {
					const dist = Math.abs(dx) + Math.abs(dy);
					if (
						dist < this.config.EXPLORATION.DIST_MIN ||
						dist > this.config.EXPLORATION.DIST_MAX
					)
						continue;
					const x = exploredTile.x + dx;
					const y = exploredTile.y + dy;
					if (x < minX || y < minY || x > maxX || y > maxY) continue;

					// Check if tile is defined (part of the map)
					if (!this.tileRepo.isTileDefined({ x, y })) continue;

					const tileIndex =
						this.tileRepo.getTileIndexByTileCoordinates({ x, y });
					if (chestSet.has(tileIndex)) continue;
					const tile =
						this.tileRepo.getTileEntityByTileIndex(tileIndex);
					if (
						tile &&
						tilerepoSalvageableFrameNames.has(tile.frameName)
					)
						continue;
					const visibility = this.tileRepo.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					);
					if (
						visibility === TileVisibilityType.BLACK ||
						visibility === TileVisibilityType.FOG
					) {
						candidates.push({ x, y });
					}
				}
			}

			if (candidates.length > 0) {
				return candidates[randomInteger(0, candidates.length - 1)];
			}

			for (
				let dx = -this.config.EXPLORATION.SCAN_RADIUS;
				dx <= this.config.EXPLORATION.SCAN_RADIUS;
				dx++
			) {
				for (
					let dy = -this.config.EXPLORATION.SCAN_RADIUS;
					dy <= this.config.EXPLORATION.SCAN_RADIUS;
					dy++
				) {
					const x = exploredTile.x + dx;
					const y = exploredTile.y + dy;
					if (x < minX || y < minY || x > maxX || y > maxY) continue;

					// Check if tile is defined (part of the map)
					if (!this.tileRepo.isTileDefined({ x, y })) continue;

					const tileIndex =
						this.tileRepo.getTileIndexByTileCoordinates({ x, y });
					if (chestSet.has(tileIndex)) continue;
					const visibility = this.tileRepo.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					);
					if (visibility === TileVisibilityType.EXPLORED) {
						const frameName = this.tileRepo.getFrameNameByTileId(
							this.tileRepo.getTileNumberByLayer(tileIndex, 0),
						);
						if (
							this.config.ALLOWED_ON_EXPLORED.has(
								frameName as FrameName,
							)
						) {
							return { x, y };
						}
					}
				}
			}
		} else if (
			type === ChestType.REWARDED_AD ||
			(type === ChestType.FIXED && exploredTile)
		) {
			// Generate chest randomly in a 10/10 square around the area. - GD did not defined this behaviour.
			const candidates: Array<TileCoordinates> = [];
			for (let dx = -15; dx <= 15; dx++) {
				for (let dy = -15; dy <= 15; dy++) {
					const x = exploredTile!.x + dx;
					const y = exploredTile!.y + dy;
					if (x < minX || y < minY || x > maxX || y > maxY) continue;

					// Check if tile is defined (part of the map)
					if (!this.tileRepo.isTileDefined({ x, y })) continue;

					const tileIndex =
						this.tileRepo.getTileIndexByTileCoordinates({ x, y });
					const visibility = this.tileRepo.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					);
					if (
						visibility === TileVisibilityType.BLACK ||
						visibility === TileVisibilityType.FOG
					) {
						candidates.push({ x, y });
					} else if (visibility === TileVisibilityType.EXPLORED) {
						const frameName = this.tileRepo.getFrameNameByTileId(
							this.tileRepo.getTileNumberByLayer(tileIndex, 0),
						);
						if (
							this.config.ALLOWED_ON_EXPLORED.has(
								frameName as FrameName,
							)
						) {
							candidates.push({ x, y });
						}
					}
				}
			}

			if (candidates.length > 0) {
				return candidates[randomInteger(0, candidates.length - 1)];
			}
		}
		// zakomentovane kvoli sentry (4.4k) - potrebujeme to evidovat ako error?
		// throw new Error(
		// 	"Chest coordinates not found. Please check the configuration and tile visibility.",
		// );
		return null;
	}

	private generateReward(isSpecial: boolean, type?: ChestType): ChestReward {
		if (type === ChestType.SPECIAL) {
			return this.generateSpecialReward();
		}
		const resourceStore = useResourcesStore();
		const { coinsIncome } = storeToRefs(resourceStore);
		const base = isSpecial
			? this.config.REWARD.SPECIAL_BASE
			: this.config.REWARD.BASE;
		const passiveIncome = coinsIncome.value ?? 1;

		const multiplier = isSpecial
			? this.config.REWARD.SPECIAL_PASSIVE_INCOME_MULTIPLIER
			: this.config.REWARD.PASSIVE_INCOME_MULTIPLIER;
		const rand =
			Math.random() *
				(this.config.REWARD.RAND_MAX - this.config.REWARD.RAND_MIN) +
			this.config.REWARD.RAND_MIN;

		const coins = ceilNumber((base + passiveIncome * multiplier) * rand);

		const reward: ChestReward = { coins };

		if (Math.random() < this.config.REWARD.SUPER_CLICKERS_CHANCE) {
			reward[ResourceType.SUPER_CLICKER] = randomInteger(
				this.config.REWARD.SUPER_CLICKERS_MIN,
				this.config.REWARD.SUPER_CLICKERS_MAX,
			);
		}
		return reward;
	}

	private generateSpecialReward(): ChestReward {
		const resourceStore = useResourcesStore();
		const { coinsIncome } = storeToRefs(resourceStore);
		const base = this.config.REWARD.SPECIAL_BASE;
		const passiveIncome = coinsIncome.value ?? 1;

		const rand =
			Math.random() *
				(this.config.REWARD.RAND_MAX - this.config.REWARD.RAND_MIN) +
			this.config.REWARD.RAND_MIN;
		const coins = ceilNumber(
			(base +
				passiveIncome *
					this.config.REWARD.SPECIAL_PASSIVE_INCOME_MULTIPLIER) *
				rand,
		);
		const gems = randomInteger(
			this.config.REWARD.SPECIAL_GEMS_MIN,
			this.config.REWARD.SPECIAL_GEMS_MAX,
		);
		const superClicker = randomInteger(
			this.config.REWARD.SPECIAL_SUPER_CLICKERS_MIN,
			this.config.REWARD.SPECIAL_SUPER_CLICKERS_MAX,
		);

		return { coins, gems, [ResourceType.SUPER_CLICKER]: superClicker };
	}

	private canGenerateSpecialChest(type: ChestType): boolean {
		if (type === ChestType.FIXED) return true;
		return (
			type !== ChestType.REWARDED_AD &&
			Math.random() < this.config.SPECIAL_CHEST.CHANCE &&
			Date.now() > this.chestStore.specialChestCooldownUntil
		);
	}

	private generateChestId(): string {
		return Math.random().toString(36).substring(2, 12);
	}
}

export default ChestRepository;
